import type { HexCell, Point } from "@/src/types/map";

export interface AxialHex {
  q: number;
  r: number;
}

export const hexRadius = (gridSize: number) => gridSize / Math.sqrt(3);

export const axialToWorld = (hex: AxialHex, gridSize: number): Point => [
  gridSize * (hex.q + hex.r / 2),
  gridSize * (Math.sqrt(3) / 2) * hex.r,
];

export const hexCorners = (hex: AxialHex, gridSize: number): Point[] => {
  const center = axialToWorld(hex, gridSize);
  const radius = hexRadius(gridSize);
  return Array.from({ length: 6 }, (_, index) => {
    const angle = ((index * 60 - 30) * Math.PI) / 180;
    return [
      center[0] + radius * Math.cos(angle),
      center[1] + radius * Math.sin(angle),
    ];
  });
};

const roundAxial = (fractionalQ: number, fractionalR: number): AxialHex => {
  const fractionalS = -fractionalQ - fractionalR;
  let q = Math.round(fractionalQ);
  let r = Math.round(fractionalR);
  let s = Math.round(fractionalS);
  const qDifference = Math.abs(q - fractionalQ);
  const rDifference = Math.abs(r - fractionalR);
  const sDifference = Math.abs(s - fractionalS);

  if (qDifference > rDifference && qDifference > sDifference) q = -r - s;
  else if (rDifference > sDifference) r = -q - s;
  else s = -q - r;

  return { q, r };
};

export const worldToAxial = (point: Point, gridSize: number): AxialHex => {
  const radius = hexRadius(gridSize);
  const q = ((Math.sqrt(3) / 3) * point[0] - point[1] / 3) / radius;
  const r = ((2 / 3) * point[1]) / radius;
  return roundAxial(q, r);
};

export const hexKey = ({ q, r }: AxialHex) => `${q},${r}`;

export const visibleHexes = (
  left: number,
  top: number,
  right: number,
  bottom: number,
  gridSize: number,
): AxialHex[] => {
  const verticalStep = gridSize * (Math.sqrt(3) / 2);
  const rMin = Math.floor(top / verticalStep) - 2;
  const rMax = Math.ceil(bottom / verticalStep) + 2;
  const hexes: AxialHex[] = [];
  for (let r = rMin; r <= rMax; r += 1) {
    const qMin = Math.floor(left / gridSize - r / 2) - 2;
    const qMax = Math.ceil(right / gridSize - r / 2) + 2;
    for (let q = qMin; q <= qMax; q += 1) hexes.push({ q, r });
  }
  return hexes;
};

export const terrainBounds = (cells: HexCell[], gridSize: number) => {
  if (cells.length === 0) return null;
  const radius = hexRadius(gridSize);
  return cells.reduce((bounds, cell) => {
    const [x, y] = axialToWorld(cell, gridSize);
    bounds.minX = Math.min(bounds.minX, x - gridSize / 2);
    bounds.maxX = Math.max(bounds.maxX, x + gridSize / 2);
    bounds.minY = Math.min(bounds.minY, y - radius);
    bounds.maxY = Math.max(bounds.maxY, y + radius);
    return bounds;
  }, {
    minX: Number.POSITIVE_INFINITY,
    minY: Number.POSITIVE_INFINITY,
    maxX: Number.NEGATIVE_INFINITY,
    maxY: Number.NEGATIVE_INFINITY,
  });
};
